import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import React from "react";
import {
  useCreatePricingPlanMutation,
  useGetPricingPlanQuery,
  useUpdatePricingPlanMutation,
} from "@/store/admin_api";
import { PricingPlan } from "@/model/interface/PricingPlan";
import { emptyPlan } from "./emptyPlan";
import PlanIdentitySection from "./PlanIdentitySection";
import PlanTelephonyRatesSection from "./PlanTelephonyRatesSection";
import PlanAiOrchestratorRatesSection from "./PlanAiOrchestratorRatesSection";
import PlanDataRatesSection from "./PlanDataRatesSection";
import PlanTextingRatesSection from "./PlanTextingRatesSection";

export default function EditPricingPlan() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id;
  const numericId = id ? Number(id) : undefined;

  const { data: plan, isLoading } = useGetPricingPlanQuery(numericId!, { skip: isNew });
  const [createPlan, { isLoading: isCreating }] = useCreatePricingPlanMutation();
  const [updatePlan, { isLoading: isUpdating }] = useUpdatePricingPlanMutation();
  const isSaving = isCreating || isUpdating;

  const [editing, setEditing] = useState<Partial<PricingPlan>>({ ...emptyPlan });

  useEffect(() => {
    if (plan) {
      setEditing({ ...plan });
    }
  }, [plan]);

  const setField = (field: string, value: unknown) => {
    setEditing((previous) => ({ ...previous, [field]: value }));
  };

  const handleSave = async () => {
    if (!editing.name) {
      toast.error("Name is required");
      return;
    }
    try {
      if (editing.id) {
        await updatePlan({ id: editing.id, data: editing }).unwrap();
        toast.success("Pricing plan updated");
      } else {
        await createPlan(editing).unwrap();
        toast.success("Pricing plan created");
      }
      navigate("/platform-admin/pricing");
    } catch (saveError) {
      const apiError = saveError as { data?: { detail?: string } };
      toast.error(apiError?.data?.detail || "Failed to save");
    }
  };

  if (!isNew && isLoading) {
    return (
      <div className="space-y-6 p-4">
        <Skeleton className="h-10 w-1/3" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 max-w-4xl">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate("/platform-admin/pricing")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold tracking-tight">
            {isNew ? "New Pricing Plan" : editing.name || "Pricing Plan"}
          </h1>
          <p className="text-muted-foreground">
            Base rates and markup configuration for usage-based billing.
          </p>
        </div>
      </div>

      <PlanIdentitySection editing={editing} onField={setField} />
      <PlanTelephonyRatesSection editing={editing} onField={setField} />
      <PlanAiOrchestratorRatesSection editing={editing} onField={setField} />
      <PlanDataRatesSection editing={editing} onField={setField} />
      <PlanTextingRatesSection editing={editing} onField={setField} />

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => navigate("/platform-admin/pricing")}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
          {isNew ? "Create" : "Save Changes"}
        </Button>
      </div>
    </div>
  );
}
